import { Injectable } from '@angular/core';
import { IFeatures, Permission } from '@bciti/types';
import { Store } from '@ngrx/store';
import { IndividualConfig } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { GeoidService } from 'src/app/global/components/forms/geoid-address/service/geoid.abstract.service';
import { SearchAddressPayload } from 'src/app/global/components/forms/geoid-address/service/geoid.model';
import { Organisation, OrganisationConfiguration } from 'src/app/global/global.model';
import { GetOrganisationConfigurationAction, RefreshOrganisationAction, SetOrganisationAction, SetOrganisationConfigurationAction } from 'src/app/global/store/global.actions';
import { Actions, ofType } from '@ngrx/effects';
import { map } from 'rxjs/operators';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import {
    availableLanguagesSelector,
    languageSelector,
    organisationConfigurationSelector,
    organisationFeaturesConfigurationSelector,
    organisationSelector,
} from 'src/app/global/store/global.selectors';
import { State } from 'src/app/store';
import { UserService } from 'src/app/user/service/user.abstract.service';
import { profileSelector, userRecordSelector } from 'src/app/user/store/user.selectors';
import { authPermissionsSelector } from 'src/app/auth/store/auth.selectors';
import { Address, Profile, UserRecord } from 'src/app/user/user.model';
import { authOrgSelector } from 'src/app/auth/store/auth.selectors';
import { BcitiContextService } from './context.abstract.service';

@Injectable()
export class BcitiContextServiceImpl implements BcitiContextService {
    constructor(
        private store: Store<State>,
        private messageService: MessageService,
        private geoidService: GeoidService,
        private actions$: Actions,
        private userService: UserService,
    ) {}

    public uploadProfilePicture(file: File): Observable<string> {
        return this.userService.uploadProfilePicture(file);
    }

    public getUserPermissions(): Observable<Permission[]> {
        return this.store.select(authPermissionsSelector);
    }

    public searchAddress(address: SearchAddressPayload): Observable<Address[]> {
        return this.geoidService.searchAddress(address);
    }

    public forceSearchAddress(address: SearchAddressPayload): Observable<Address[]> {
        return this.geoidService.forceSearchAddress(address);
    }

    public searchAddressQuery(query: string): Observable<Address[]> {
        return this.geoidService.searchAddressQuery(query);
    }

    public getUserRecord(): Observable<UserRecord> {
        return this.store.select(userRecordSelector);
    }

    public getProfile(): Observable<Profile> {
        return this.store.select(profileSelector);
    }

    public getLanguage(): Observable<string> {
        return this.store.select(languageSelector);
    }

    public getOrganisationConfiguration(): Observable<OrganisationConfiguration> {
        return this.store.select(organisationConfigurationSelector);
    }

    public getOrganisationFeaturesConfiguration(): Observable<IFeatures> {
        return this.store.select(organisationFeaturesConfigurationSelector);
    }

    public async getOrganisation(): Promise<{ city: string; bounds: any }> {
        const organisation: any = await new Promise((resolve) => {
            this.store.select(organisationSelector).subscribe((org) => resolve(org));
        });

        return {
            city: organisation.tag,
            ...organisation.bounds,
        };
    }

    public getOrganisationObs(): Observable<Partial<Organisation>> {
        return this.store.select(organisationSelector);
    }

    public getOrganisationTagFromAuth(): Observable<string> {
        return this.store.select(authOrgSelector);
    }

    public getAvailableLanguages(): Observable<string[]> {
        return this.store.select(availableLanguagesSelector);
    }

    public successMessage(message: string, title?: string, options?: Partial<IndividualConfig>): void {
        this.messageService.success(message, title, options);
    }

    public warningMessage(message: string, title?: string, options?: Partial<IndividualConfig>): void {
        this.messageService.warning(message, title, options);
    }

    public infoMessage(message: string, title?: string, options?: Partial<IndividualConfig>): void {
        this.messageService.info(message, title, options);
    }

    public errorMessage(message: string, title?: string, options?: Partial<IndividualConfig>): void {
        this.messageService.error(message, title, options);
    }

    public refreshOrganisation(): Observable<Organisation> {
        this.store.dispatch(RefreshOrganisationAction());
        return this.actions$.pipe(
            ofType(SetOrganisationAction),
            map(({ organisation }) => organisation),
        );
    }

    public refreshOrganisationConfiguration(): Observable<OrganisationConfiguration> {
        this.store.dispatch(GetOrganisationConfigurationAction());
        return this.actions$.pipe(
            ofType(SetOrganisationConfigurationAction),
            map(({ configuration }) => configuration),
        );
    }

    // assets
    public getBcitiPlusLogo(type?: 'app' | 'dark' | 'light'): string {
        switch (type) {
            case 'app':
                return 'assets/images/bciti-app-logo.svg';
            case 'light':
                return 'assets/images/bciti-logo-light.svg';
            case 'dark':
            default:
                return 'assets/images/bciti-logo.svg';
        }
    }

    // admin advanced search
    public adminAdvancedSearch(queryObject: object) {
        return this.userService.advancedSearch(queryObject);
    }
}

export const BCITI_CONTEXT_SERVICE_PROVIDER = {
    provide: BcitiContextService,
    useClass: BcitiContextServiceImpl,
};
